import React, { ForwardedRef, forwardRef, JSX, SyntheticEvent, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Typography, useTheme } from '@mui/material';
import { TreeViewItemId } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { subscribe, unsubscribe } from 'pubsub-js';
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { OBJECT_CHANGED_TOPIC, ObjectChangedMsg } from '../../../common/models/pub-sub/PubSubTopics';
import { contains } from '../../../common/utilities/Contains';
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from '../../../contexts/user-context/UserContext';
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { INavigationItem } from '../../../services/navigation-data-service/Models';
import NavigationDataService from '../../../services/navigation-data-service/NavigationDataService';
import { getFlattenedTreeData } from './GroupHierarclyUtils';
import { GroupTreeItem } from './GroupTreeItem';

export type GroupHierarchyV2RefType = {
    expandAll: () => void;
    collapseAll: () => void;
}

export type GroupHierarchyV2Props = {
    onSelected: (selectedItem: INavigationItem, shiftKey: boolean, ctrlKey: boolean) => void;
    enableDynamicGroupRefresh?: boolean;
    onLoading?: (isLoading: boolean) => void;
    defaultSelected?: number;
    hiddenGroups?: number[];
    rootID?: number;
    filterText?: string;
    onExpanded?: () => void;
    disableDefault?: boolean
}

function GroupHierarchyV2({
    onSelected,
    onLoading,
    enableDynamicGroupRefresh,
    defaultSelected,
    hiddenGroups,
    rootID,
    filterText,
    onExpanded,
    disableDefault
}: GroupHierarchyV2Props, ref?: ForwardedRef<GroupHierarchyV2RefType>): JSX.Element {

    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();

    const treeViewRef = useRef<HTMLUListElement>(null);

    const [treeData, setTreeData] = useState<INavigationItem>();
    const [selected, setSelected] = useState<string | undefined>(() => {
        if (disableDefault) {
            return undefined;
        }

        return defaultSelected?.toString() ?? routeContext.groupID?.toString();
    });

    const [expanded, setExpanded] = useState<string[]>([]);
    const [groups, setGroups] = useState<INavigationItem[] | undefined>();

    const theme = useTheme();

    useImperativeHandle<GroupHierarchyV2RefType, GroupHierarchyV2RefType>(
        ref,
        () => ({
            expandAll: () => expandAll(),
            collapseAll: () => collapseAll()
        })
    );

    useEffect(() => {

        const getChildren = (groups: INavigationItem[], parent: INavigationItem) => {

            const children = groups.filter(o => o.parentID === parent.objectID);

            if (children) {
                parent.children = [];

                children.forEach((value: INavigationItem/*, index: number*/) => {
                    if (value.hasChildren) {
                        getChildren(groups, value);
                    }

                    // if (parent.objectID === 2341) {
                    //     console.log(JSON.stringify(value));
                    // }
                    parent.children?.push(value);
                });
            }
        }

        const getTreeData = async (customerID: number, groupID: number) => {

            let allGroups = await NavigationDataService.getNavigationGroups(customerID, offlineState.offline);

            if (hiddenGroups && hiddenGroups.length) {
                allGroups = allGroups.filter(o => hiddenGroups.find(v => v === o.objectID) === undefined);
            }

            const data = allGroups.find(o => o.objectID === groupID);

            if (data) {
                getChildren(allGroups, data);
            }

            setGroups(allGroups);

            setTreeData(data);
        };

        if ((userState.customer && userState.user?.baseGroup?.id) || offlineState.offlineDataLoaded) {
            getTreeData(userState.customer?.id as number,
                rootID ?? userState.user?.baseGroup?.id as number);
        }

        const onObjectChanged = (objectChangedData: ObjectChangedMsg) => {

            //console.log('GroupHierarchy - onObjectChanged');

            // the changed object is a group and the name is not set, then the change likely came from
            // group admin so rebuild the breadcrumb
            // if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group &&
            //     objectChangedData.name === undefined) {
            if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group) {
                getTreeData(userState.customer?.id as number,
                    userState.user?.baseGroup?.id as number);
            }
        }

        const token = subscribe(OBJECT_CHANGED_TOPIC, (message, data) => onObjectChanged(data));

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }

    }, [
        userState.customer,
        userState.user?.baseGroup?.id,
        offlineState,
        setGroups,
        hiddenGroups,
        rootID
    ]);

    useEffect(() => {

        if (onLoading) {
            onLoading(treeData === undefined);
        }
    }, [treeData,
        onLoading
    ]);

    useEffect(() => {

        const getItemsToExpand = async (customerID: number, groupID: number) => {

            let itemsToExpand: string[] = [];

            const contextGroup = await NavigationDataService.getNavigationGroup(customerID,
                groupID, {
                includeChildren: true,
                includeAncestors: true
            });

            if (contextGroup?.ancestors) {
                itemsToExpand = contextGroup?.ancestors?.map(o => o.objectID.toString());
            }

            itemsToExpand.push(groupID.toString());
            setExpanded(itemsToExpand.reverse());
        };

        const groupID = defaultSelected ?? routeContext.groupID;
        if (expanded.length === 0 && groupID) {
            getItemsToExpand(userState.customer?.id as number, groupID);
        }

    }, [groups,
        userState.customer,
        routeContext.groupID,
        defaultSelected,
        expanded,
        setExpanded,
        onExpanded]);

    useEffect(() => {

        if (!disableDefault) {
            const item = defaultSelected?.toString() ?? routeContext.groupID?.toString();

            if (item !== selected) {
                setSelected(item);
            }
        }   
    }, [
        routeContext.groupID,
        selected,
        setSelected,
        defaultSelected,
        disableDefault
    ]);


    const onExpandedItemsChange = (_event: React.SyntheticEvent | null, itemIds: TreeViewItemId[]) => {
        setExpanded(itemIds);
        onExpanded?.();
    };

    const onLabelClick = (event: SyntheticEvent<Element, MouseEvent> | null, item: INavigationItem) => {        

        if (item.allowClick) {
            onSelected(item, event?.nativeEvent?.shiftKey ?? false, event?.nativeEvent?.ctrlKey ?? false);
            setSelected(item.objectID.toString());
        }

        event?.preventDefault();
    };

    const expandAll = useCallback(() => {
        if (treeData) {
            const flattenedTree = getFlattenedTreeData(treeData);
            const allNodeIds = flattenedTree.map(node => node.objectID.toString());
            setExpanded(allNodeIds);
        }
    }, [
        treeData
    ]);

    const collapseAll = useCallback(() => {
        if (treeData) {
            // Keep the root node expanded when collapsing all
            setExpanded([treeData.objectID.toString()]);
        }
    }, [
        treeData
    ]);

    useEffect(() => {
        if ((filterText?.length ?? 0) > 0) {
            expandAll();
        }
    }, [
        treeData,
        expandAll,
        filterText
    ]);

    const data = useMemo(() => {

        function getFilteredTreeData(
            node: INavigationItem,
            filterText: string | undefined
        ): INavigationItem | null {

            if ((filterText?.length ?? 0) === 0) {
                return node;
            } else {
                const matches = contains(node.title, filterText);
                const filteredChildren = node.children
                    ? node.children
                        .map(child => getFilteredTreeData(child, filterText))
                        .filter(child => child !== null)
                    : [];

                if (matches || filteredChildren.length > 0) {
                    return {
                        ...node,
                        children: filteredChildren.length > 0
                            ? filteredChildren
                            : undefined,
                        matchesFilter: matches

                    };
                }

                return null;
            }
        }

        return treeData
            ? (filterText?.length ?? 0) === 0
                ? treeData
                : getFilteredTreeData(treeData, filterText)
            : undefined;
    }, [
        treeData,
        filterText
    ])

    const renderTree = (item: INavigationItem) => (
        <GroupTreeItem key={item.objectID}
            itemId={item.objectID.toString()}
            showIcon={true}
            // 2021-02-08 tried load on demand, but, tree does not handle state updates correctly, specifically the expanded state
            //endIcon={item.hasChildren && <Box component='span' className={classes.toggleIcon} onClick={(e) => onLoadTreeItem(e, item)}><ChevronRightIcon /></Box>}
            navigationItem={item}
            includeObjectCount={true}
            enableDynamicGroupRefresh={enableDynamicGroupRefresh && !offlineState.offline}
            onLabelClick={item.allowClick ? (e: React.MouseEvent<Element, MouseEvent>) => onLabelClick(e, item) : undefined}
            color={(item.matchesFilter ?? true) === false
                ? theme.palette.text.disabled
                : theme.palette.text.primary
            }
        >
            {
                Array.isArray(item.children) ?
                    item.children.map((node) => renderTree(node)) :
                    null
            }
        </GroupTreeItem>
    );

    return (
        <>
            {data
                ? (
                    <SimpleTreeView
                        ref={treeViewRef}
                        sx={{
                            flexGrow: 1,
                            width: 400,
                            [theme.breakpoints.only('xs')]: {
                                width: '100%'
                            }
                        }}
                        slots={{
                            collapseIcon: ExpandMoreIcon,
                            expandIcon: ChevronRightIcon
                        }}
                        expandedItems={expanded}
                        selectedItems={selected}
                        onExpandedItemsChange={onExpandedItemsChange}
                    >
                        {
                            data && renderTree(data)
                        }
                    </SimpleTreeView>
                )
                : (
                    <Typography
                        variant='body2'
                        sx={{
                            paddingLeft: 2,
                            paddingTop: 1,
                            paddingBottom: 1,
                            paddingRight: 2
                        }}
                    //color='textSecondary' sx={{ p: 2 }}
                    >
                        No Groups Found
                    </Typography>
                )
            }
        </>
    );
}

export default forwardRef<GroupHierarchyV2RefType, GroupHierarchyV2Props>(GroupHierarchyV2);